import { therapyEN, therapyHR } from "../therapy/translations";
import { authEN, authHR } from "../auth/translations";

export const resources = {
  en: {
    translation: {
      therapy: therapyEN,
      auth: authEN,
      common: {
        appName: "Health Tracker",
        tagline: "Private health journal",
        automatic: "Automatic",
        close: "Close",
        cancel: "Cancel",
        optional: "optional",
        private: "Private",
      },
      nav: {
        overview: "Overview",
        history: "History",
        trends: "Trends",
        preferences: "Preferences",
      },
      shell: {
        skipToContent: "Skip to content",
        primaryNavigation: "Primary navigation",
        localWorkspace: "Local workspace",
        keycloakPending: "Keycloak connection pending",
      },
      actions: {
        logRecord: "Log a record",
        exportPdf: "Export PDF",
        tryAgain: "Try again",
        editRecord: "Edit {{kind}} record",
        deleteRecord: "Delete {{kind}} record",
        addRecordForDate: "Add record for this day",
      },
      recordKinds: {
        weight: "Weight",
        vitals: "Blood pressure",
        exercise: "Exercise",
        appointment: "Appointment",
        short: {
          weight: "Weight",
          vitals: "Blood pressure",
          exercise: "Exercise",
          appointment: "Appointment",
        },
      },
      dashboard: {
        healthOverview: "Health overview",
        overviewDescription: "Your latest readings, meaningful changes, and upcoming care in one place.",
        latestReadings: "Latest readings",
        totalRecords_one: "{{count}} record total",
        totalRecords_few: "{{count}} records total",
        totalRecords_other: "{{count}} records total",
        weight: "Weight",
        bloodPressure: "Blood pressure",
        exerciseThisWeek: "Exercise this week",
        upcomingVisits: "Upcoming appointments",
        minutes_one: "minute",
        minutes_few: "minutes",
        minutes_other: "minutes",
        appointments_one: "appointment",
        appointments_few: "appointments",
        appointments_other: "appointments",
        addReadingForComparison: "Add another reading to see the change.",
        weightChange: "{{value}} {{unit}} since the previous reading",
        pressureChange: "{{value}} mmHg systolic since the previous reading",
        exerciseChange: "{{value}} min versus the same time last week",
        nextAppointment: "Next: {{date}}",
        noUpcomingAppointments: "No upcoming appointments",
        openTrend: "Open trend",
        openHistory: "Open history",
        viewTrend: "View {{kind}} trend",
        viewHistory: "View {{kind}} history",
        bloodPressureCategories: {
          nonElevated: "Not elevated",
          elevated: "Elevated",
          hypertension: "Hypertension range",
        },
        bloodPressureDisclaimer: "Reference categories describe a single reading and are not a diagnosis or medical advice.",
        bloodPressureSource: "2024 ESC patient guidance",
        noEntriesForDate: "No entries on this date",
        noEntriesForDateDescription: "Choose another date or add a new record.",
        loading: "Loading your health overview",
      },
      recordForm: {
        title: "Log a record",
        subtitle: "Add a measurement, activity, or appointment.",
        recordType: "Record type",
        dateTime: "Date and time",
        date: "Date",
        time: "Time",
        weight: "Weight in {{unit}}",
        systolic: "Systolic",
        diastolic: "Diastolic",
        pulse: "Pulse",
        exerciseName: "Activity",
        appointmentName: "Appointment",
        durationMinutes: "Duration in minutes",
        notes: "Notes",
        placeholders: {
          weight: "e.g. 82.4",
          systolic: "e.g. 120",
          diastolic: "e.g. 80",
          pulse: "e.g. 64",
          exercise: "e.g. Morning walk",
          appointment: "e.g. Annual check-up",
          notes: "Add useful context…",
          duration: "e.g. 45",
        },
        save: "Save {{kind}}",
        saving: "Saving…",
        updating: "Saving changes…",
        saveChanges: "Save changes",
        editTitle: "Edit health record",
        editSubtitle: "Correct the values, date, or notes for this record.",
        saved: "Health record saved.",
        updated: "Health record updated.",
        saveFailed: "The record could not be saved. Check the values and try again.",
      },
      calendar: {
        title: "Calendar",
        thisMonth: "This month",
        monthDescription: "Select a date to review every entry from that day.",
        navigation: "Month navigation",
        previousMonth: "Previous month",
        nextMonth: "Next month",
        today: "Today",
        entries: "Day entries",
        records_one: "{{count}} record",
        records_few: "{{count}} records",
        records_other: "{{count}} records",
        moreRecords_one: "+{{count}} more entry",
        moreRecords_few: "+{{count}} more entries",
        moreRecords_other: "+{{count}} more entries",
        selectDate: "{{date}}: {{records}}",
      },
      datePicker: {
        noDate: "Choose a date",
        dialogLabel: "Choose {{label}}",
        navigation: "Calendar navigation",
        nextMonth: "Next month",
        previousMonth: "Previous month",
        today: "Today",
        clear: "Clear",
      },
      timeline: {
        emptyTitle: "Your timeline starts here",
        emptyDescription: "Log a weight, blood-pressure reading, exercise session, or appointment.",
        pulse: "Pulse {{value}} bpm",
        duration: "{{count}} minutes",
        deleteConfirm: "Delete this {{kind}} record?",
        deleteTitle: "Delete record",
        deletePrompt: "Delete this record? This action cannot be undone.",
        deleting: "Deleting…",
        confirmDelete: "Delete record",
        deleteFailed: "The record could not be deleted. Try again.",
      },
      history: {
        eyebrow: "Your health archive",
        title: "History",
        description: "Browse measurements, activities, and appointments in one chronological record.",
        filterLabel: "Filter health history",
        kindFilterLabel: "Filter by record type",
        searchLabel: "Search history",
        searchPlaceholder: "Search activities, appointments, and notes",
        searchAction: "Search",
        fromDate: "From date",
        toDate: "To date",
        preparingExport: "Preparing export…",
        exportLoadFailed: "The complete history could not be loaded for export. Try again.",
        loadOlder: "Load older records",
        loadingOlder: "Loading older records…",
        loadOlderFailed: "Older records could not be loaded. Try again.",
        filters: {
          all: "All",
        },
        emptyTitle: "No matching records",
        emptyDescription: "Choose another filter or add a new record from the overview.",
        loading: "Loading health history",
        showingDate: "Showing records for {{date}}",
        clearDate: "Clear date",
        clearFilters: "Clear filters",
        visibleRecords_one: "{{count}} visible record",
        visibleRecords_few: "{{count}} visible records",
        visibleRecords_other: "{{count}} visible records",
        loadedRecords_one: "{{count}} record loaded; older records are available",
        loadedRecords_few: "{{count}} records loaded; older records are available",
        loadedRecords_other: "{{count}} records loaded; older records are available",
        loadedOfTotal: "{{loaded}} of {{total}} matching records loaded",
      },
      trends: {
        eyebrow: "Patterns over time",
        title: "Trends",
        description: "Simple, private insight into how your measurements and activity change over time.",
        weight: "Weight",
        systolic: "Systolic pressure",
        diastolic: "Diastolic pressure",
        bloodPressureAndPulse: "Blood pressure and pulse",
        vitalsChartHint: "The shaded band connects diastolic and systolic pressure. Dashed references mark your thresholds ({{systolic}}/{{diastolic}} mmHg). Categories are not a diagnosis or medical advice.",
        systolicReference: "Reference {{value}}",
        diastolicReference: "Reference {{value}}",
        chartLegend: "Legend for {{title}}",
        pulse: "Pulse",
        exerciseDuration: "Exercise duration",
        bpm: "bpm",
        minutesShort: "min",
        periodTitle: "Time period",
        periodDescription: "One period controls every chart, latest value, reading count, and activity total.",
        activePeriod: "Showing {{range}}",
        allHistory: "All available history",
        updating: "Updating…",
        customFrom: "From",
        customTo: "To",
        applyRange: "Apply range",
        invalidRange: "Choose a valid start and end date.",
        resultLimitNotice: "Showing the latest {{count}} records in this period. Narrow the period for complete detail.",
        periods: {
          "30d": "30 days",
          "3m": "3 months",
          "6m": "6 months",
          "1y": "1 year",
          all: "All time",
          custom: "Custom",
        },
        readings_one: "{{count}} reading",
        readings_few: "{{count}} readings",
        readings_other: "{{count}} readings",
        chartLabel: "{{title}} trend chart",
        insufficientData: "Add at least two readings to see a trend.",
        loading: "Calculating health trends",
        rangeSelector: "Visible range for {{title}}",
        statSummary: "{{prefix}}average {{average}} · range {{minimum}}–{{maximum}} · {{comparison}}",
        summaryWithComparison: "{{value}} {{unit}} vs previous period",
        summaryWithoutComparison: "no preceding period for comparison",
      },
      preferences: {
        eyebrow: "Your workspace",
        title: "Preferences",
        description: "Choose how Health Tracker looks and communicates on this device.",
        languageTitle: "Language",
        languageDescription: "Automatic follows the preferred language reported by your browser.",
        english: "English",
        croatian: "Hrvatski",
        appearanceTitle: "Appearance",
        appearanceDescription: "System follows the light or dark setting of your browser and operating system.",
        system: "System",
        light: "Light",
        dark: "Dark",
        currentLanguage: "Currently using {{language}}",
        currentTheme: "Currently using {{theme}} mode",
        localNotice: "Preferences are stored only in this browser until Keycloak user profiles are connected.",
        unitsTitle: "Units",
        unitsDescription: "Choose how body weight is entered and displayed. Data remains stored in canonical kilograms.",
        weightUnit: { kg: "Kilograms", lb: "Pounds" },
        thresholdsTitle: "Blood-pressure references",
        thresholdsDescription: "Set the home systolic and diastolic values used for contextual category chips and chart reference lines.",
        homeThresholds: "Home measurements",
        restoreEscThresholds: "Restore 2024 ESC references",
        thresholdDisclaimer: "These references provide context only. They are not a diagnosis, treatment target, or medical advice.",
        thresholdRange: "Enter {{min}}–{{max}} mmHg",
        dataTitle: "Measurement backup",
        dataDescription: "Back up weight, blood pressure, exercise and appointments as JSON. Therapy backups are managed separately below.",
        exportJson: "Export JSON",
        chooseBackup: "Choose backup",
        importConfirmation_one: "Import {{count}} record?",
        importConfirmation_few: "Import {{count}} records?",
        importConfirmation_other: "Import {{count}} records?",
        importDuplicateNotice: "Imported records are added as new entries. Existing matching entries are not overwritten.",
        confirmImport: "Import records",
        importing: "Importing…",
        importComplete_one: "Imported {{count}} record.",
        importComplete_few: "Imported {{count}} records.",
        importComplete_other: "Imported {{count}} records.",
        importFailed: "Import failed. No records were added.",
        invalidBackup: "This file is not a valid Health Tracker backup.",
        backupLimitExceeded: "Backups are limited to {{count}} records. Narrow the dataset before exporting.",
        exportComplete_one: "Exported {{count}} record.",
        exportComplete_few: "Exported {{count}} records.",
        exportComplete_other: "Exported {{count}} records.",
        exportFailed: "The JSON backup could not be created.",
      },
      report: {
        modalTitle: "Export health report",
        modalDescription: "Choose exactly which records and sections to include in your PDF.",
        dateRange: "Date range",
        from: "From",
        to: "To",
        allDates: "Leave both dates empty to include all available history.",
        recordTypes: "Record types",
        sections: "Report sections",
        summary: "Summary",
        graphs: "Graphs",
        detailedData: "Detailed data",
        matches_one: "{{count}} matching record",
        matches_few: "{{count}} matching records",
        matches_other: "{{count}} matching records",
        generating: "Generating PDF…",
        download: "Download PDF",
        selectType: "Select at least one record type.",
        selectSection: "Select at least one report section.",
        invalidRange: "The start date must be before the end date.",
        noRecords: "No records match this export selection.",
        failed: "The PDF could not be generated. Try again.",
        documentTitle: "Health history report",
        generated: "Generated {{date}}",
        period: "Period",
        allHistory: "All available history",
        includedRecords: "Included records",
        overview: "Overview",
        charts: "Health trends",
        details: "Detailed records",
        date: "Date",
        type: "Type",
        value: "Value",
        notes: "Notes",
        noNotes: "No notes",
        notEnoughChartData: "Not enough numeric data to draw this chart.",
        weightChart: "Weight",
        vitalsChart: "Blood pressure and pulse",
        pulseChart: "Pulse",
        bpm: "bpm",
        exerciseChart: "Exercise duration",
        systolic: "Systolic",
        diastolic: "Diastolic",
        systolicReference: "Reference {{value}}",
        diastolicReference: "Reference {{value}}",
        bloodPressureDisclaimer: "Your home thresholds ({{systolic}}/{{diastolic}} mmHg) are shown for context. A single reading is not a diagnosis or medical advice.",
        page: "Page {{current}} of {{total}}",
        fileName: "health-report-{{date}}.pdf",
      },
      errors: {
        title: "Health data could not be loaded",
        message: "The request could not be completed. Check the local service and try again.",
      },
    },
  },
  hr: {
    translation: {
      therapy: therapyHR,
      auth: authHR,
      common: {
        appName: "Health Tracker",
        tagline: "Privatni zdravstveni dnevnik",
        automatic: "Automatski",
        close: "Zatvori",
        cancel: "Odustani",
        optional: "neobavezno",
        private: "Privatno",
      },
      nav: {
        overview: "Pregled",
        history: "Povijest",
        trends: "Trendovi",
        preferences: "Postavke",
      },
      shell: {
        skipToContent: "Prijeđi na sadržaj",
        primaryNavigation: "Glavna navigacija",
        localWorkspace: "Lokalni radni prostor",
        keycloakPending: "Keycloak povezivanje je u pripremi",
      },
      actions: {
        logRecord: "Dodaj zapis",
        exportPdf: "Izvezi PDF",
        tryAgain: "Pokušaj ponovno",
        editRecord: "Uredi zapis: {{kind}}",
        deleteRecord: "Izbriši zapis: {{kind}}",
        addRecordForDate: "Dodaj zapis za ovaj dan",
      },
      recordKinds: {
        weight: "Tjelesna masa",
        vitals: "Krvni tlak",
        exercise: "Vježbanje",
        appointment: "Termin",
        short: {
          weight: "Tjelesna masa",
          vitals: "Krvni tlak",
          exercise: "Vježbanje",
          appointment: "Termin",
        },
      },
      dashboard: {
        healthOverview: "Pregled zdravlja",
        overviewDescription: "Najnovija mjerenja, važne promjene i nadolazeća skrb na jednom mjestu.",
        latestReadings: "Najnovija mjerenja",
        totalRecords_one: "Ukupno {{count}} zapis",
        totalRecords_few: "Ukupno {{count}} zapisa",
        totalRecords_other: "Ukupno {{count}} zapisa",
        weight: "Tjelesna masa",
        bloodPressure: "Krvni tlak",
        exerciseThisWeek: "Vježbanje ovaj tjedan",
        upcomingVisits: "Nadolazeći termini",
        minutes_one: "minuta",
        minutes_few: "minute",
        minutes_other: "minuta",
        appointments_one: "termin",
        appointments_few: "termina",
        appointments_other: "termina",
        addReadingForComparison: "Dodajte još jedno mjerenje za prikaz promjene.",
        weightChange: "{{value}} {{unit}} od prethodnog mjerenja",
        pressureChange: "{{value}} mmHg sistolički od prethodnog mjerenja",
        exerciseChange: "{{value}} min u odnosu na isto vrijeme prošlog tjedna",
        nextAppointment: "Sljedeći: {{date}}",
        noUpcomingAppointments: "Nema nadolazećih termina",
        openTrend: "Otvori trend",
        openHistory: "Otvori povijest",
        viewTrend: "Prikaži trend: {{kind}}",
        viewHistory: "Prikaži povijest: {{kind}}",
        bloodPressureCategories: {
          nonElevated: "Nije povišen",
          elevated: "Povišen",
          hypertension: "Raspon hipertenzije",
        },
        bloodPressureDisclaimer: "Referentne kategorije opisuju pojedinačno mjerenje i nisu dijagnoza ni medicinski savjet.",
        bloodPressureSource: "Smjernice ESC-a za pacijente iz 2024.",
        noEntriesForDate: "Nema zapisa za ovaj datum",
        noEntriesForDateDescription: "Odaberite drugi datum ili dodajte novi zapis.",
        loading: "Učitavanje zdravstvenog pregleda",
      },
      recordForm: {
        title: "Dodaj zdravstveni zapis",
        subtitle: "Unesite mjerenje, aktivnost ili termin.",
        recordType: "Vrsta zapisa",
        dateTime: "Datum i vrijeme",
        date: "Datum",
        time: "Vrijeme",
        weight: "Tjelesna masa u {{unit}}",
        systolic: "Sistolički",
        diastolic: "Dijastolički",
        pulse: "Puls",
        exerciseName: "Aktivnost",
        appointmentName: "Termin",
        durationMinutes: "Trajanje u minutama",
        notes: "Bilješke",
        placeholders: {
          weight: "npr. 82,4",
          systolic: "npr. 120",
          diastolic: "npr. 80",
          pulse: "npr. 64",
          exercise: "npr. Jutarnja šetnja",
          appointment: "npr. Godišnji pregled",
          notes: "Dodajte koristan kontekst…",
          duration: "npr. 45",
        },
        save: "Spremi: {{kind}}",
        saving: "Spremanje…",
        updating: "Spremanje promjena…",
        saveChanges: "Spremi promjene",
        editTitle: "Uredi zdravstveni zapis",
        editSubtitle: "Ispravite vrijednosti, datum ili bilješke zapisa.",
        saved: "Zdravstveni zapis je spremljen.",
        updated: "Zdravstveni zapis je ažuriran.",
        saveFailed: "Zapis nije moguće spremiti. Provjerite vrijednosti i pokušajte ponovno.",
      },
      calendar: {
        title: "Kalendar",
        thisMonth: "Ovaj mjesec",
        monthDescription: "Odaberite datum za pregled svih zapisa toga dana.",
        navigation: "Navigacija po mjesecima",
        previousMonth: "Prethodni mjesec",
        nextMonth: "Sljedeći mjesec",
        today: "Danas",
        entries: "Dnevni zapisi",
        records_one: "{{count}} zapis",
        records_few: "{{count}} zapisa",
        records_other: "{{count}} zapisa",
        moreRecords_one: "+još {{count}} zapis",
        moreRecords_few: "+još {{count}} zapisa",
        moreRecords_other: "+još {{count}} zapisa",
        selectDate: "{{date}}: {{records}}",
      },
      datePicker: {
        noDate: "Odaberite datum",
        dialogLabel: "Odaberite: {{label}}",
        navigation: "Navigacija kalendara",
        nextMonth: "Sljedeći mjesec",
        previousMonth: "Prethodni mjesec",
        today: "Danas",
        clear: "Ukloni",
      },
      timeline: {
        emptyTitle: "Vaša vremenska crta započinje ovdje",
        emptyDescription: "Dodajte tjelesnu masu, krvni tlak, vježbanje ili termin.",
        pulse: "Puls {{value}} otk./min",
        duration: "{{count}} minuta",
        deleteConfirm: "Izbrisati zapis: {{kind}}?",
        deleteTitle: "Brisanje zapisa",
        deletePrompt: "Izbrisati ovaj zapis? Ovu radnju nije moguće poništiti.",
        deleting: "Brisanje…",
        confirmDelete: "Izbriši zapis",
        deleteFailed: "Zapis nije moguće izbrisati. Pokušajte ponovno.",
      },
      history: {
        eyebrow: "Vaša zdravstvena arhiva",
        title: "Povijest",
        description: "Pregledajte mjerenja, aktivnosti i termine u jednoj kronološkoj evidenciji.",
        filterLabel: "Filtriraj zdravstvenu povijest",
        kindFilterLabel: "Filtriraj prema vrsti zapisa",
        searchLabel: "Pretraži povijest",
        searchPlaceholder: "Pretraži aktivnosti, termine i bilješke",
        searchAction: "Pretraži",
        fromDate: "Od datuma",
        toDate: "Do datuma",
        preparingExport: "Priprema izvoza…",
        exportLoadFailed: "Cjelovitu povijest nije moguće učitati za izvoz. Pokušajte ponovno.",
        loadOlder: "Učitaj starije zapise",
        loadingOlder: "Učitavanje starijih zapisa…",
        loadOlderFailed: "Starije zapise nije moguće učitati. Pokušajte ponovno.",
        filters: {
          all: "Sve",
        },
        emptyTitle: "Nema odgovarajućih zapisa",
        emptyDescription: "Odaberite drugi filtar ili dodajte novi zapis s pregleda.",
        loading: "Učitavanje zdravstvene povijesti",
        showingDate: "Prikaz zapisa za {{date}}",
        clearDate: "Ukloni datum",
        clearFilters: "Ukloni filtre",
        visibleRecords_one: "{{count}} prikazani zapis",
        visibleRecords_few: "{{count}} prikazana zapisa",
        visibleRecords_other: "{{count}} prikazanih zapisa",
        loadedRecords_one: "Učitan je {{count}} zapis; dostupni su stariji zapisi",
        loadedRecords_few: "Učitana su {{count}} zapisa; dostupni su stariji zapisi",
        loadedRecords_other: "Učitano je {{count}} zapisa; dostupni su stariji zapisi",
        loadedOfTotal: "Učitano {{loaded}} od {{total}} odgovarajućih zapisa",
      },
      trends: {
        eyebrow: "Obrasci kroz vrijeme",
        title: "Trendovi",
        description: "Jednostavan i privatan uvid u promjene mjerenja i aktivnosti.",
        weight: "Tjelesna masa",
        systolic: "Sistolički tlak",
        diastolic: "Dijastolički tlak",
        bloodPressureAndPulse: "Krvni tlak i puls",
        vitalsChartHint: "Osjenčano područje povezuje dijastolički i sistolički tlak. Isprekidane reference označavaju vaše pragove ({{systolic}}/{{diastolic}} mmHg). Kategorije nisu dijagnoza ni medicinski savjet.",
        systolicReference: "Referenca {{value}}",
        diastolicReference: "Referenca {{value}}",
        chartLegend: "Legenda za {{title}}",
        pulse: "Puls",
        exerciseDuration: "Trajanje vježbanja",
        bpm: "otk./min",
        minutesShort: "min",
        periodTitle: "Vremensko razdoblje",
        periodDescription: "Jedno razdoblje upravlja svim grafovima, zadnjim vrijednostima, brojem mjerenja i ukupnom aktivnošću.",
        activePeriod: "Prikaz: {{range}}",
        allHistory: "Cijela dostupna povijest",
        updating: "Ažuriranje…",
        customFrom: "Od",
        customTo: "Do",
        applyRange: "Primijeni razdoblje",
        invalidRange: "Odaberite ispravan početni i završni datum.",
        resultLimitNotice: "Prikazuje se najnovijih {{count}} zapisa u ovom razdoblju. Suzite razdoblje za potpune detalje.",
        periods: {
          "30d": "30 dana",
          "3m": "3 mjeseca",
          "6m": "6 mjeseci",
          "1y": "1 godina",
          all: "Sve vrijeme",
          custom: "Prilagođeno",
        },
        readings_one: "{{count}} mjerenje",
        readings_few: "{{count}} mjerenja",
        readings_other: "{{count}} mjerenja",
        chartLabel: "Graf trenda: {{title}}",
        insufficientData: "Dodajte barem dva mjerenja za prikaz trenda.",
        loading: "Izračun zdravstvenih trendova",
        rangeSelector: "Vidljivi raspon za {{title}}",
        statSummary: "{{prefix}}prosjek {{average}} · raspon {{minimum}}–{{maximum}} · {{comparison}}",
        summaryWithComparison: "{{value}} {{unit}} u odnosu na prethodno razdoblje",
        summaryWithoutComparison: "nema prethodnog razdoblja za usporedbu",
      },
      preferences: {
        eyebrow: "Vaš radni prostor",
        title: "Postavke",
        description: "Odaberite izgled i jezik aplikacije Health Tracker na ovom uređaju.",
        languageTitle: "Jezik",
        languageDescription: "Automatski odabir prati željeni jezik koji prijavljuje preglednik.",
        english: "English",
        croatian: "Hrvatski",
        appearanceTitle: "Izgled",
        appearanceDescription: "Sustav prati svijetlu ili tamnu postavku preglednika i operacijskog sustava.",
        system: "Sustav",
        light: "Svijetlo",
        dark: "Tamno",
        currentLanguage: "Trenutačni jezik: {{language}}",
        currentTheme: "Trenutačni način prikaza: {{theme}}",
        localNotice: "Postavke se spremaju samo u ovom pregledniku dok ne povežemo Keycloak korisničke profile.",
        unitsTitle: "Mjerne jedinice",
        unitsDescription: "Odaberite kako se tjelesna masa unosi i prikazuje. Podaci se i dalje spremaju u kanonskim kilogramima.",
        weightUnit: { kg: "Kilogrami", lb: "Funte" },
        thresholdsTitle: "Referentne vrijednosti krvnog tlaka",
        thresholdsDescription: "Postavite kućne sistoličke i dijastoličke vrijednosti za kontekstualne kategorije i referentne crte na grafovima.",
        homeThresholds: "Kućna mjerenja",
        restoreEscThresholds: "Vrati referentne vrijednosti ESC-a 2024.",
        thresholdDisclaimer: "Ove vrijednosti služe samo kao kontekst. Nisu dijagnoza, cilj liječenja ni medicinski savjet.",
        thresholdRange: "Unesite {{min}}–{{max}} mmHg",
        dataTitle: "Sigurnosna kopija mjerenja",
        dataDescription: "Spremite masu, krvni tlak, aktivnosti i preglede u JSON kopiju. Kopijama terapija upravlja se zasebno ispod.",
        exportJson: "Izvezi JSON",
        chooseBackup: "Odaberi kopiju",
        importConfirmation_one: "Uvesti {{count}} zapis?",
        importConfirmation_few: "Uvesti {{count}} zapisa?",
        importConfirmation_other: "Uvesti {{count}} zapisa?",
        importDuplicateNotice: "Uvezeni zapisi dodaju se kao novi. Postojeći podudarni zapisi neće se prebrisati.",
        confirmImport: "Uvezi zapise",
        importing: "Uvoz…",
        importComplete_one: "Uvezen je {{count}} zapis.",
        importComplete_few: "Uvezena su {{count}} zapisa.",
        importComplete_other: "Uvezeno je {{count}} zapisa.",
        importFailed: "Uvoz nije uspio. Nijedan zapis nije dodan.",
        invalidBackup: "Datoteka nije valjana sigurnosna kopija aplikacije Health Tracker.",
        backupLimitExceeded: "Sigurnosna kopija ograničena je na {{count}} zapisa. Suzite skup podataka prije izvoza.",
        exportComplete_one: "Izvezen je {{count}} zapis.",
        exportComplete_few: "Izvezena su {{count}} zapisa.",
        exportComplete_other: "Izvezeno je {{count}} zapisa.",
        exportFailed: "JSON sigurnosnu kopiju nije moguće izraditi.",
      },
      report: {
        modalTitle: "Izvoz zdravstvenog izvještaja",
        modalDescription: "Odaberite zapise i dijelove koje želite uključiti u PDF.",
        dateRange: "Raspon datuma",
        from: "Od",
        to: "Do",
        allDates: "Ostavite oba datuma prazna za uključivanje cijele dostupne povijesti.",
        recordTypes: "Vrste zapisa",
        sections: "Dijelovi izvještaja",
        summary: "Sažetak",
        graphs: "Grafovi",
        detailedData: "Detaljni podaci",
        matches_one: "{{count}} odgovarajući zapis",
        matches_few: "{{count}} odgovarajuća zapisa",
        matches_other: "{{count}} odgovarajućih zapisa",
        generating: "Izrada PDF-a…",
        download: "Preuzmi PDF",
        selectType: "Odaberite barem jednu vrstu zapisa.",
        selectSection: "Odaberite barem jedan dio izvještaja.",
        invalidRange: "Početni datum mora biti prije završnog datuma.",
        noRecords: "Nijedan zapis ne odgovara odabiru za izvoz.",
        failed: "PDF nije moguće izraditi. Pokušajte ponovno.",
        documentTitle: "Izvještaj zdravstvene povijesti",
        generated: "Izrađeno {{date}}",
        period: "Razdoblje",
        allHistory: "Cijela dostupna povijest",
        includedRecords: "Uključeni zapisi",
        overview: "Sažetak",
        charts: "Zdravstveni trendovi",
        details: "Detaljni zapisi",
        date: "Datum",
        type: "Vrsta",
        value: "Vrijednost",
        notes: "Bilješke",
        noNotes: "Bez bilješke",
        notEnoughChartData: "Nema dovoljno brojčanih podataka za izradu grafa.",
        weightChart: "Tjelesna masa",
        vitalsChart: "Krvni tlak i puls",
        pulseChart: "Puls",
        bpm: "otk./min",
        exerciseChart: "Trajanje vježbanja",
        systolic: "Sistolički",
        diastolic: "Dijastolički",
        systolicReference: "Referenca {{value}}",
        diastolicReference: "Referenca {{value}}",
        bloodPressureDisclaimer: "Vaši kućni pragovi ({{systolic}}/{{diastolic}} mmHg) prikazani su kao kontekst. Pojedinačno mjerenje nije dijagnoza ni medicinski savjet.",
        page: "Stranica {{current}} od {{total}}",
        fileName: "zdravstveni-izvjestaj-{{date}}.pdf",
      },
      errors: {
        title: "Zdravstvene podatke nije moguće učitati",
        message: "Zahtjev nije moguće dovršiti. Provjerite lokalnu uslugu i pokušajte ponovno.",
      },
    },
  },
} as const;

export type SupportedLanguage = keyof typeof resources;
